/**
 * Status colours as CHART FILLS: one rule, derived from STATUS_COLORS for every status, including any added later.
 *
 * WHY A SECOND STEP EXISTS AT ALL. STATUS_COLORS (lib/api.js) are PILL BACKGROUNDS: each is validated to carry
 * WHITE TEXT ("check it against white here before shipping it"). A bar on a chart has the opposite job -- it must
 * read against the CARD it sits on. Those values were never validated for that, and on the dark card one of them
 * (6, Revisions In Progress, #7A3348) is 2.04:1, a bar you have to hunt for.
 *
 * THE RULE. For each status colour and each card: if it already clears CHART_FILL_FLOOR against the card, it is
 * used unchanged. If not, it is stepped along ITS OWN HUE -- OKLCH lightness moved away from the card, hue and
 * chroma kept (chroma only lowered if sRGB cannot hold it at that lightness) -- to the first step that clears the
 * floor, measured off the hex that comes out. One list and one helper, not a hand-picked value for the one status
 * someone noticed: a new status inherits the treatment instead of needing to be noticed (same reasoning as
 * 103e11fe). There is exactly one definition per status; the chart step is computed from it, never stored.
 */
export const CHART_FILL_FLOOR = 3;                        // WCAG 1.4.11, a graphical object against its ground
export const CHART_CARD = { dark: '#171717', light: '#ffffff' };   // .ug-card under the site look (site-look.css)

const toLin = c => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const toSrgb = c => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);
const hexToLin = h => [1, 3, 5].map(i => toLin(parseInt(h.slice(i, i + 2), 16) / 255));
const linToHex = rgb => '#' + rgb.map(c => Math.round(Math.min(1, Math.max(0, toSrgb(c))) * 255).toString(16).padStart(2, '0')).join('');

export function contrastRatio(a, b) {
  const Y = h => { const [r, g, bl] = hexToLin(h); return 0.2126 * r + 0.7152 * g + 0.0722 * bl; };
  const [hi, lo] = [Y(a), Y(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

export function toOklch(hex) {
  const [r, g, b] = hexToLin(hex);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s;
  return { L, C: Math.hypot(A, B), H: ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360 };
}

// OKLCH -> linear sRGB, or null when sRGB cannot hold it (so a step never quietly bends the hue by clipping)
function oklchToLin(L, C, H) {
  const A = C * Math.cos((H * Math.PI) / 180), B = C * Math.sin((H * Math.PI) / 180);
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s = (L - 0.0894841775 * A - 1.2914855480 * B) ** 3;
  const rgb = [4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
               -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
               -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s];
  return rgb.every(c => c >= -1e-6 && c <= 1 + 1e-6) ? rgb : null;
}

/** The step of one identity colour against one ground: unchanged if it already clears `floor`, otherwise moved along
 *  its own hue until it does. 3:1 for a bar (the default); 4.5:1 for TEXT (lib/roleInk.js). */
export function chartFill(hex, card, floor = CHART_FILL_FLOOR) {
  if (contrastRatio(hex, card) >= floor) return hex;
  const { L, C, H } = toOklch(hex);
  const dir = toOklch(card).L < 0.5 ? 1 : -1;               // away from the card: lighter on dark, darker on light
  for (let k = 1; k <= 200; k++) {
    const Lk = L + dir * k * 0.005;
    if (Lk <= 0 || Lk >= 1) break;
    for (let c = C; c >= 0; c -= 0.002) {                    // keep chroma; lower it only as far as sRGB requires
      const lin = oklchToLin(Lk, c, H);
      if (!lin) continue;
      const out = linToHex(lin);
      if (contrastRatio(out, card) >= floor) return out;
      break;
    }
  }
  throw new Error(`no step of ${hex} clears ${floor}:1 on ${card}`);
}

/** A bar whose status cannot be identified: a PURE neutral (chroma 0), so it can never be read as a status, and
 *  one value that clears the floor on both cards (3.78:1 dark, 4.74:1 light). The first choice here was a pale
 *  grey for light mode at 2.54:1 -- the unknown bar would have been the one bar nobody could see. */
export const UNMATCHED_FILL = { dark: '#737373', light: '#737373' };

/**
 * Bars for "Projects by Status". `statusCounts` is the server's {label: count} (keyed by LABEL -- see
 * routes/analytics.py); `labels` is STAFF_CLIENT_STATUSES (the names the server counts by), `colors` STATUS_COLORS. A label that maps to no status
 * paints UNMATCHED_FILL: an unknown bar must be visibly unknown, never borrow a neighbour's colour.
 */
export function statusBars(statusCounts, labels, colors, isLight) {
  const idByLabel = Object.fromEntries(Object.entries(labels).map(([id, label]) => [label, id]));
  const card = CHART_CARD[isLight ? 'light' : 'dark'];
  return Object.entries(statusCounts || {}).map(([name, count]) => {
    const id = Object.prototype.hasOwnProperty.call(idByLabel, name) ? idByLabel[name] : null;
    const base = id !== null ? colors[id] : null;
    return {
      name: name.replace(/\s+/g, '\n'), count, id: id === null ? null : Number(id),
      fill: base ? chartFill(base, card) : UNMATCHED_FILL[isLight ? 'light' : 'dark'],
    };
  });
}

/** A status colour as TEXT (a group's name on the board), stepped along its own hue to 4.5:1 against the worst ground
 *  text sits on in that theme -- the palest dark ground, the darkest light one. Same rule as the bar fills, a text
 *  floor. "Revisions In Progress" was 2.12:1 as text on the dark board. */
export const TEXT_FLOOR = 4.5;
const TEXT_WORST = { dark: '#1e1e1e', light: '#ebebed' };
export const statusInk = (hex, isLight) => (hex ? chartFill(hex, TEXT_WORST[isLight ? 'light' : 'dark'], TEXT_FLOOR) : hex);

/** A STATUS PILL's fill: the status's own hue, stepped (only if it must be) until the pill's WHITE ink reads at
 * TEXT_FLOOR. STATUS_COLORS are validated for white already and come back unchanged; what this catches is a status
 * with no colour, whose '#94A3B8' fallback carried white at 2.56:1 -- every editor's "Assigned" pill (editor status 0
 * has no STATUS_COLORS entry), measured when the site look reached editors on 2026-09-29. */
export const pillFill = hex => (hex ? chartFill(hex, '#ffffff', TEXT_FLOOR) : hex);
